import React from 'react';
import { JobPosting } from '../types';
import { X, MapPin, Clock, DollarSign, CheckCircle2, Send, Award, Briefcase } from 'lucide-react';

interface JobDetailModalProps {
  job: JobPosting | null;
  onClose: () => void;
  onApply: (job: JobPosting) => void;
}

export const JobDetailModal: React.FC<JobDetailModalProps> = ({ job, onClose, onApply }) => {
  if (!job) return null;

  const deptClass = job.department.replace(/\s+/g, '');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>
          <X size={18} />
        </button>

        <div style={{ marginBottom: '24px' }}>
          <span className={`dept-badge ${deptClass}`}>{job.department}</span>
          <h2 style={{ fontSize: '1.8rem', fontWeight: 800, marginTop: '8px', lineHeight: 1.2 }}>
            {job.title}
          </h2>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '20px',
              marginTop: '14px',
              fontSize: '0.9rem',
              color: 'var(--text-muted)',
              borderBottom: '1px solid var(--border-color)',
              paddingBottom: '20px',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <MapPin size={16} color="var(--primary-cyan)" /> {job.location}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Clock size={16} color="var(--accent-purple)" /> {job.type}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Award size={16} color="var(--accent-amber)" /> {job.experience_level}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--accent-emerald)', fontWeight: 700 }}>
              <DollarSign size={16} /> {job.salary_range || 'Competitive'}
            </span>
          </div>
        </div>

        {/* Overview */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '10px', color: 'var(--primary-cyan)' }}>
            Role Overview
          </h3>
          <p style={{ color: 'var(--text-main)', fontSize: '0.95rem', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
            {job.description}
          </p>
        </div>

        {/* Responsibilities */}
        {job.responsibilities && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '10px', color: 'var(--accent-purple)' }}>
              Key Responsibilities
            </h3>
            <p style={{ color: 'var(--text-main)', fontSize: '0.95rem', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
              {job.responsibilities}
            </p>
          </div>
        )}

        {/* Requirements */}
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '10px', color: 'var(--accent-emerald)' }}>
            Requirements & Skills
          </h3>
          <p style={{ color: 'var(--text-main)', fontSize: '0.95rem', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
            {job.requirements}
          </p>
        </div>

        {/* Action Button */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid var(--border-color)',
            paddingTop: '20px',
          }}
        >
          <div>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-dim)' }}>
              Posted on {new Date(job.created_at).toLocaleDateString()}
            </span>
          </div>

          <button
            className="btn-primary"
            style={{ padding: '12px 28px', fontSize: '1rem' }}
            onClick={() => {
              onClose();
              onApply(job);
            }}
          >
            <Send size={18} /> Apply For This Position
          </button>
        </div>
      </div>
    </div>
  );
};
